import React from 'react';
import Pagination from './Pagination';

export interface Column {
  key: string;
  title: React.ReactNode;
  dataIndex: string;
  width?: string;
  headerAlign?: 'left' | 'center' | 'right';
  cellAlign?: 'left' | 'center' | 'right';
  fontWeight?: string;
  render?: (data: any) => React.ReactNode;
}

interface PaginationConfig {
  total: number;
  current: number;
  pageSize: number;
  onChange: (page: number, pageSize?: number) => void;
  showSizeChanger?: boolean;
  onShowSizeChange?: (current: number, size: number) => void;
}

interface TableProps {
  columns: Column[];
  dataSource: any[];
  pagination?: PaginationConfig;
  onRowClick?: (data: any) => void;
  rowClassName?: string;
}

const Table: React.FC<TableProps> = ({
  columns,
  dataSource,
  pagination,
  onRowClick,
  rowClassName,
}) => {
  return (
    <div>
      <div className="flex flex-col -z-50">
        <div className="overflow-x-auto pb-2 mb-2">
          <div className="min-w-full inline-block align-middle">
            <div className="overflow-hidden rounded-3xl border border-gray-200 mt-1">
              <table className="table-auto min-w-full rounded-3xl">
                <thead>
                  <tr className="bg-[#F4F4F4] flex justify-start items-center border px-2 h-[48px] relative">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="font-semibold text-sm leading-6 font-[Manrope] px-2 py-4 text-black opacity-[0.87]"
                        style={{
                          width: column.width || `${100 / columns.length}%`,
                          textAlign: column.headerAlign || 'left',
                        }}
                      >
                        {column.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {dataSource.map((data, index) => (
                    <tr
                      key={index}
                      className={`bg-white transition-all px-2 duration-500 hover:bg-gray-50 border flex justify-start items-center relative ${rowClassName ? rowClassName : ''}`}
                      onClick={() => onRowClick?.(data)}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="whitespace-nowrap overflow-hidden px-2 py-4 text-sm font-medium text-gray-900 font-[Manrope]"
                          style={{
                            textAlign: column.cellAlign || 'left',
                            width: column.width || `${100 / columns.length}%`,
                            fontWeight: column.fontWeight,
                          }}
                        >
                          {column.render
                            ? column.render(data)
                            : data[column.dataIndex]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {pagination && (
        <div className="mt-4">
          <Pagination
            totalItems={pagination.total}
            itemsPerPage={pagination.pageSize}
            currentPage={pagination.current}
            onPageChange={(page) => {
              pagination.onChange(page, pagination.pageSize);
            }}
            showSizeChanger={pagination.showSizeChanger}
            onSizeChange={(size) => {
              pagination.onShowSizeChange?.(1, size);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
